// Tenant-scoped portal-user CRUD at `/users`. Visible to tenant_admin
// AND master_admin operating with a tenant scope. Backend route is
// `/api/v1/users` — auto-scoped via JWT for tenant users and via
// explicit per-request `x-tenant-context` for masters.
//
// P1-5 (Agent C): mirror the AdminUsersPage shape but:
//   - no master_admin role option (backend rejects; UI hides)
//   - no tenant-move action (tenant is implicit)
//   - no skipTenantScope flag — we DO want the scope header
//
// Resident creation is explicitly routed to /residents (residents are
// not provisioned via this surface in any site_mode).

import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError, type ApiRequestInit } from '../lib/api.js';
import { getAllEmployees } from '../lib/employees.js';
import { rowMenuStyle } from '../lib/rowMenuStyle.js';
import { useTenantScopedPage } from '../lib/useTenantScopedPage.js';
import { useAuth } from '../auth/AuthContext.js';

type Role = 'master_admin' | 'tenant_admin' | 'tenant_manager' | 'tenant_viewer' | 'resident';

// Roles a tenant_admin or scoped master_admin can assign through this
// surface. `master_admin` is NOT in this list — backend enforces and the
// UI must never even offer it.
const TENANT_ASSIGNABLE_ROLES: Role[] = ['tenant_admin', 'tenant_manager', 'tenant_viewer'];

interface UserRow {
  id: string;
  email: string;
  name: string;
  role: Role;
  tenant_id: string | null;
  status: 'invited' | 'active' | 'suspended';
  mfa_enrolled: boolean;
  last_login_at: string | null;
  created_at: string;
}

interface EmployeeSummary {
  id: string;
  email: string;
  name: string;
  role: 'employee' | 'manager';
  status: 'invited' | 'active' | 'suspended' | 'offboarded';
}

const ROLE_TONE: Record<Role, string> = {
  master_admin: 'bg-primary-container text-on-primary',
  tenant_admin: 'bg-secondary-container text-on-secondary-container',
  tenant_manager: 'bg-tertiary-fixed text-on-tertiary-fixed',
  tenant_viewer: 'bg-surface-container-high text-on-surface-variant',
  resident: 'bg-surface-container-low text-on-surface',
};

const STATUS_TONE: Record<UserRow['status'], string> = {
  invited: 'bg-tertiary-fixed text-on-tertiary-fixed',
  active: 'bg-secondary-container text-on-secondary-container',
  suspended: 'bg-error-container text-on-error-container',
};

async function loadUsers(scopeOpts?: ApiRequestInit): Promise<UserRow[]> {
  const res = await apiClient.get<{ data: UserRow[] }>('/api/v1/users', scopeOpts);
  return res.data;
}

async function loadEmployeeSummaries(scopeOpts?: ApiRequestInit): Promise<EmployeeSummary[]> {
  try {
    const res = await getAllEmployees<EmployeeSummary>(scopeOpts, 200);
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

type ActionKind =
  | 'invite'
  | 'edit'
  | 'role'
  | 'suspend'
  | 'delete'
  | 'reactivate'
  | 'reset-password'
  | 'reset-mfa'
  | 'invalidate'
  | 'resend-invite';
type MenuActionKind = ActionKind | 'open-employee' | 'create-employee';

export function UsersPage() {
  const { state } = useAuth();
  const navigate = useNavigate();
  const {
    scopeOpts,
    canLoadData,
    effectiveTenantId,
    canShowTenantActions,
    canExecuteTenantActions,
    isMasterWithoutContext,
    scopedDetailHref,
  } = useTenantScopedPage();
  const [rows, setRows] = useState<UserRow[] | null>(null);
  const [employees, setEmployees] = useState<EmployeeSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [action, setAction] = useState<{
    kind: ActionKind;
    user: UserRow | null;
  } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    if (!canLoadData || !effectiveTenantId) {
      setRows([]);
      setEmployees([]);
      setError(null);
      return () => {
        cancelled = true;
      };
    }
    setRows(null);
    setError(null);
    Promise.all([loadUsers(scopeOpts), loadEmployeeSummaries(scopeOpts)])
      .then(([u, e]) => {
        if (cancelled) return;
        setRows(u);
        setEmployees(e);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load users');
      });
    return () => {
      cancelled = true;
    };
  }, [canLoadData, effectiveTenantId, reloadTick, scopeOpts]);

  const selfId = state.status === 'authed' ? state.user.id : null;

  // Visible rows: hide master_admin entries from this tenant-scoped
  // surface even on the off-chance backend returns one (it shouldn't —
  // `/api/v1/users` filters by tenant_id and masters live at
  // tenant_id=null).
  const visibleRows = useMemo(() => (rows ?? []).filter((u) => u.role !== 'master_admin'), [rows]);
  const employeeByEmail = useMemo(() => {
    const m = new Map<string, EmployeeSummary>();
    for (const employee of employees) m.set(employee.email.toLowerCase(), employee);
    return m;
  }, [employees]);

  function handleAction(user: UserRow, kind: MenuActionKind) {
    if (kind === 'open-employee') {
      const employee = employeeByEmail.get(user.email.toLowerCase());
      const href = employee && scopedDetailHref('/employees', employee.id, undefined, user.tenant_id);
      if (href) navigate(href);
      return;
    }
    if (kind === 'create-employee') {
      const params = new URLSearchParams({
        tenantId: user.tenant_id ?? effectiveTenantId ?? '',
        name: user.name,
        email: user.email,
        role: user.role === 'tenant_admin' || user.role === 'tenant_manager' ? 'manager' : 'employee',
      });
      navigate(`/credentials/enroll?${params}`);
      return;
    }
    setAction({ kind, user });
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
            <Icon name="group" className="text-2xl" />
          </div>
          <div>
            <h1 className="font-headline-md text-headline-md text-on-surface">Managers</h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Portal accounts for this tenant. For resident accounts use the{' '}
              <Link to="/residents" className="underline">
                Residents
              </Link>{' '}
              workflow.
            </p>
          </div>
        </div>
        {canShowTenantActions ? (
          <button
            type="button"
            onClick={() => setAction({ kind: 'invite', user: null })}
            disabled={!canExecuteTenantActions}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary font-label-md text-label-md rounded-lg hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:pointer-events-none"
          >
            <Icon name="person_add" />
            <span>Invite manager</span>
          </button>
        ) : null}
      </header>

      {toast ? (
        <div
          role="status"
          className="bg-secondary-container text-on-secondary-container rounded-lg px-4 py-2 font-body-md text-body-md flex items-center gap-2"
        >
          <Icon name="check_circle" />
          <span className="flex-1">{toast}</span>
          <button type="button" onClick={() => setToast(null)} className="underline font-medium">
            Dismiss
          </button>
        </div>
      ) : null}

      {isMasterWithoutContext ? (
        <section className="rounded-lg border border-outline-variant bg-surface-container-lowest p-6">
          <h2 className="font-title-md text-title-md text-on-surface">Choose a tenant</h2>
          <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
            Open a tenant from the Tenants page to manage that tenant's managers.
          </p>
          <Link
            to="/tenants"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 font-label-md text-label-md text-on-primary"
          >
            <Icon name="apartment" />
            Tenants
          </Link>
        </section>
      ) : null}

      {!isMasterWithoutContext ? (
      <section className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden">
        {error ? (
          <div className="p-6 text-error font-body-md text-body-md" role="alert">
            {error}
          </div>
        ) : rows == null ? (
          <div className="p-12 flex justify-center">
            <Spinner />
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="p-12 text-center text-on-surface-variant font-body-md text-body-md">
            No teammates yet. Invite the first one with the button above.
          </div>
        ) : (
          <>
            <div className="md:hidden divide-y divide-outline-variant">
              {visibleRows.map((u) => {
                const employee = employeeByEmail.get(u.email.toLowerCase()) ?? null;
                return (
                  <div key={u.id} className="px-4 py-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <Link
                          to={scopedDetailHref('/users', u.id, undefined, u.tenant_id) ?? '/users'}
                          className="text-primary font-semibold hover:underline"
                          data-testid={`user-row-link-mobile-${u.id}`}
                        >
                          {u.name}
                        </Link>
                        <p className="text-sm text-on-surface-variant break-all">{u.email}</p>
                      </div>
                      <ActionMenu
                        user={u}
                        isSelf={u.id === selfId}
                        onAction={(kind) => handleAction(u, kind)}
                        employee={employee}
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${ROLE_TONE[u.role]}`}
                      >
                        {u.role}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${STATUS_TONE[u.status]}`}
                      >
                        {u.status}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-on-surface-variant">
                      <span>
                        Last login:{' '}
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : '—'}
                      </span>
                      {employee ? (
                        <Link to={scopedDetailHref('/employees', employee.id, undefined, u.tenant_id) ?? '/employees'} className="text-primary underline">
                          Employee record
                        </Link>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-container">
                  <tr>
                    <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[22%]">
                      Name
                    </th>
                    <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3">
                      Email
                    </th>
                    <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[16%]">
                      Role
                    </th>
                    <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[12%]">
                      Status
                    </th>
                    <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[14%]">
                      Last login
                    </th>
                    <th className="text-right font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[6%]">
                      &nbsp;
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant">
                  {visibleRows.map((u) => (
                    <tr key={u.id} className="hover:bg-surface-container-low transition-colors">
                      <td className="px-4 py-3 font-body-md text-body-md text-on-surface">
                        <Link
                          to={scopedDetailHref('/users', u.id, undefined, u.tenant_id) ?? '/users'}
                          className="text-primary hover:underline"
                          data-testid={`user-row-link-${u.id}`}
                        >
                          {u.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 font-body-md text-body-md text-on-surface">
                        {u.email}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${ROLE_TONE[u.role]}`}
                        >
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${STATUS_TONE[u.status]}`}
                        >
                          {u.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-body-md text-body-md text-on-surface-variant">
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ActionMenu
                          user={u}
                          isSelf={u.id === selfId}
                          onAction={(kind) => handleAction(u, kind)}
                          employee={employeeByEmail.get(u.email.toLowerCase()) ?? null}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
      ) : null}

      {action ? (
        <ActionModal
          state={action}
          scopeOpts={scopeOpts}
          onCancel={() => setAction(null)}
          onDone={(msg) => {
            setAction(null);
            setToast(msg);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ActionMenu({
  user,
  isSelf,
  onAction: dispatchAction,
  employee,
}: {
  user: UserRow;
  isSelf: boolean;
  onAction: (kind: MenuActionKind) => void;
  employee: EmployeeSummary | null;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  function onAction(kind: MenuActionKind) {
    setOpen(false);
    dispatchAction(kind);
  }
  useEffect(() => {
    if (!open) return;
    function onClickAway(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    const close = () => setOpen(false);
    document.addEventListener('mousedown', onClickAway);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close);
    return () => {
      document.removeEventListener('mousedown', onClickAway);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close);
    };
  }, [open]);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(current => !current)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Actions"
        className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-surface-container-high"
      >
        <Icon name="more_vert" />
      </button>
      {open ? (
        <div
          role="menu"
          style={rowMenuStyle(ref.current)}
          className="bg-surface-container-lowest border border-outline-variant rounded-lg shadow-lg z-30 py-1"
        >
          <MenuItem label="Edit name" onClick={() => onAction('edit')} />
          {employee ? (
            <MenuItem label="Open employee record" onClick={() => onAction('open-employee')} />
          ) : (
            <MenuItem label="Add employee record" onClick={() => onAction('create-employee')} />
          )}
          <MenuItem
            label="Change role"
            onClick={() => onAction('role')}
            disabled={isSelf}
            disabledTitle="Use the auth surface to change your own account"
          />
          {user.status === 'suspended' ? (
            <MenuItem label="Reactivate" onClick={() => onAction('reactivate')} disabled={isSelf} />
          ) : (
            <MenuItem
              label="Suspend"
              destructive
              onClick={() => onAction('suspend')}
              disabled={isSelf}
              disabledTitle="Cannot suspend yourself"
            />
          )}
          <MenuItem
            label="Delete permanently"
            destructive
            onClick={() => onAction('delete')}
            disabled={isSelf}
            disabledTitle="Cannot delete yourself"
          />
          <MenuItem
            label="Set password"
            onClick={() => onAction('reset-password')}
            disabled={isSelf}
          />
          <MenuItem label="Reset TOTP" onClick={() => onAction('reset-mfa')} disabled={isSelf} />
          <MenuItem label="Invalidate sessions" onClick={() => onAction('invalidate')} />
          {user.status === 'invited' ? (
            <MenuItem label="Resend invite" onClick={() => onAction('resend-invite')} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  disabled,
  disabledTitle,
  destructive,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  disabledTitle?: string;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      title={disabled ? disabledTitle : undefined}
      className={`w-full text-left px-3 py-2 font-body-md text-body-md hover:bg-surface-container-low disabled:opacity-50 disabled:cursor-not-allowed ${
        destructive === true ? 'text-error' : 'text-on-surface'
      }`}
    >
      {label}
    </button>
  );
}

interface ActionModalProps {
  state: { kind: ActionKind; user: UserRow | null };
  scopeOpts?: ApiRequestInit;
  onCancel: () => void;
  onDone: (toast: string) => void;
}

function ActionModal({ state, scopeOpts, onCancel, onDone }: ActionModalProps) {
  const { kind, user } = state;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-action local state.
  const [email, setEmail] = useState('');
  const [name, setName] = useState(user?.name ?? '');
  const [initialPassword, setInitialPassword] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const initialRole: Role =
    user?.role && TENANT_ASSIGNABLE_ROLES.includes(user.role) ? user.role : 'tenant_viewer';
  const [role, setRole] = useState<Role>(initialRole);
  const [inviteResult, setInviteResult] = useState<{
    user: UserRow;
    invite_token: string | null;
  } | null>(null);

  // Friendly hint surfaced when backend rejects a last-active-tenant-admin
  // suspension/role-change. Matches the message shape used by both the
  // tenant /users surface and the master /admin/users surface.
  function friendly(msg: string): string {
    const m = msg.toLowerCase();
    if (m.includes('only active') && (m.includes('tenant_admin') || m.includes('master_admin'))) {
      return `${msg} Promote a second admin to this role before retrying.`;
    }
    return msg;
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const id = user?.id;
      if (kind === 'delete' && deleteConfirmation !== 'DELETE') {
        setError('Type DELETE to confirm permanent removal.');
        setSubmitting(false);
        return;
      }
      switch (kind) {
        case 'invite': {
          const res = await apiClient.post<{
            data: { user: UserRow; invite_token: string | null };
          }>(
            '/api/v1/users',
            {
              email: email.trim().toLowerCase(),
              name: name.trim(),
              role,
              initial_password: initialPassword.trim() || undefined,
            },
            scopeOpts,
          );
          if (!res.data.invite_token) {
            onDone('User created with initial password.');
            return;
          }
          setInviteResult(res.data);
          return; // hold the modal open to display the token
        }
        case 'edit':
          await apiClient.patch(`/api/v1/users/${id}`, { name: name.trim() }, scopeOpts);
          onDone('Name updated.');
          return;
        case 'role':
          await apiClient.patch(`/api/v1/users/${id}`, { role }, scopeOpts);
          onDone('Role changed. Sessions for that user were revoked.');
          return;
        case 'suspend':
          await apiClient.delete(`/api/v1/users/${id}`, scopeOpts);
          onDone('User suspended.');
          return;
        case 'delete':
          await apiClient.post(`/api/v1/users/${id}/delete`, {}, scopeOpts);
          onDone('User permanently deleted.');
          return;
        case 'reactivate':
          await apiClient.patch(`/api/v1/users/${id}`, { status: 'active' }, scopeOpts);
          onDone('User reactivated.');
          return;
        case 'reset-password':
          await apiClient.post(
            `/api/v1/users/${id}/password/reset`,
            { new_password: resetPassword },
            scopeOpts,
          );
          onDone('Password set. Existing sessions were revoked.');
          return;
        case 'reset-mfa':
          await apiClient.post(`/api/v1/users/${id}/mfa/reset`, {}, scopeOpts);
          onDone('TOTP reset. The user will be prompted to re-enroll.');
          return;
        case 'invalidate':
          await apiClient.post(`/api/v1/users/${id}/sessions/invalidate`, {}, scopeOpts);
          onDone('Sessions invalidated.');
          return;
        case 'resend-invite': {
          await apiClient.post(`/api/v1/users/${id}/invite/resend`, {}, scopeOpts);
          onDone('Invite re-sent.');
          return;
        }
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setError(friendly(msg));
      setSubmitting(false);
    }
  }

  // Invite-result confirmation step.
  if (kind === 'invite' && inviteResult) {
    return (
      <ModalShell
        title="Invite created"
        icon="mark_email_unread"
        onCancel={() => onDone('Invite created.')}
        body={
          <div className="space-y-3">
            <p className="font-body-md text-body-md text-on-surface">
              The invite email was queued. If your environment doesn't deliver email, share the link
              below directly:
            </p>
            <pre className="font-code text-code bg-surface border border-outline-variant rounded p-2 overflow-x-auto">
              {`${window.location.origin}/accept-invite?token=${inviteResult.invite_token}`}
            </pre>
            <p className="font-body-md text-body-md text-on-surface-variant">
              This token is shown only once. It won't appear after you close this dialog.
            </p>
          </div>
        }
        primaryLabel="Done"
        primaryAction={() => onDone('Invite created.')}
      />
    );
  }

  const heading: Record<ActionKind, string> = {
    invite: 'Invite manager',
    edit: `Edit ${user?.name}`,
    role: `Change role · ${user?.name}`,
    suspend: `Suspend ${user?.name}`,
    delete: `Delete ${user?.name} permanently`,
    reactivate: `Reactivate ${user?.name}`,
    'reset-password': `Set password · ${user?.name}`,
    'reset-mfa': `Reset TOTP · ${user?.name}`,
    invalidate: `Invalidate sessions · ${user?.name}`,
    'resend-invite': `Resend invite · ${user?.name}`,
  };

  const destructive =
    kind === 'suspend' ||
    kind === 'delete' ||
    kind === 'reset-password' ||
    kind === 'reset-mfa' ||
    kind === 'invalidate';
  const sessionWarn = kind === 'role';

  return (
    <ModalShell
      // eslint-disable-next-line security/detect-object-injection -- kind is a fixed union
      title={heading[kind]}
      icon={kind === 'invite' ? 'person_add' : 'edit'}
      destructive={destructive}
      onCancel={onCancel}
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {kind === 'invite' ? (
          <>
            <Field label="Email" htmlFor="uux-email">
              <input
                id="uux-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={fieldCls}
              />
            </Field>
            <Field label="Name" htmlFor="uux-name">
              <input
                id="uux-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={fieldCls}
              />
            </Field>
            <Field label="Role" htmlFor="uux-role">
              <select
                id="uux-role"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                className={fieldCls}
              >
                {TENANT_ASSIGNABLE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Initial password" htmlFor="uux-initial-password">
              <input
                id="uux-initial-password"
                type="password"
                minLength={12}
                value={initialPassword}
                onChange={(e) => setInitialPassword(e.target.value)}
                className={fieldCls}
                placeholder="Leave blank to send invite"
              />
            </Field>
            <p className="font-body-md text-body-md text-on-surface-variant">
              To create a resident account use the{' '}
              <Link to="/residents" className="underline">
                Residents
              </Link>{' '}
              workflow.
            </p>
          </>
        ) : kind === 'edit' ? (
          <Field label="Name" htmlFor="uux-name">
            <input
              id="uux-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={fieldCls}
            />
          </Field>
        ) : kind === 'role' ? (
          <Field label="Role" htmlFor="uux-role">
            <select
              id="uux-role"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className={fieldCls}
            >
              {TENANT_ASSIGNABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </Field>
        ) : kind === 'reset-password' ? (
          <Field label="New password" htmlFor="uux-reset-password">
            <input
              id="uux-reset-password"
              type="password"
              required
              minLength={8}
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              className={fieldCls}
              placeholder="8+ characters"
            />
          </Field>
        ) : kind === 'delete' ? (
          <div className="space-y-3">
            <p className="font-body-md text-body-md text-error">
              This permanently removes the portal account. It revokes every session, reset token,
              and passkey, clears MFA, and anonymizes the account email. This cannot be undone.
            </p>
            <Field label='Type DELETE to confirm' htmlFor="uux-delete-confirmation">
              <input
                id="uux-delete-confirmation"
                type="text"
                required
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                className={fieldCls}
                autoComplete="off"
              />
            </Field>
          </div>
        ) : (
          <p className="font-body-md text-body-md text-on-surface">
            {kind === 'suspend'
              ? 'The user will be locked out and their sessions revoked. The action is audited.'
              : kind === 'reactivate'
                ? 'The user will be able to sign in again. They keep their old role.'
                : kind === 'reset-mfa'
                  ? 'Clears the TOTP enrollment and revokes existing sessions. The user must re-enroll on next sign-in.'
                  : kind === 'invalidate'
                    ? 'Drops every active session for this user. They will have to sign in again.'
                    : kind === 'resend-invite'
                      ? 'The user will receive a fresh invite email with a new token.'
                      : 'Confirm this account update.'}
          </p>
        )}
        {sessionWarn ? (
          <div
            role="status"
            className="bg-tertiary-fixed text-on-tertiary-fixed rounded-lg px-3 py-2 font-body-md text-body-md"
          >
            <Icon name="warning" className="inline-block mr-1" />
            Sessions for this user will be revoked.
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="text-error font-body-md text-body-md">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            {kind === 'invite'
              ? 'Send invite'
              : kind === 'delete'
                ? 'Delete permanently'
                : destructive
                  ? 'Confirm'
                  : 'Save'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

const fieldCls =
  'w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg font-body-md text-body-md focus:ring-2 focus:ring-primary outline-none';

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={htmlFor} className="font-label-md text-label-md text-on-surface block">
        {label}
      </label>
      {children}
    </div>
  );
}

function ModalShell({
  title,
  icon,
  destructive,
  onCancel: _onCancel,
  children,
  body,
  primaryLabel,
  primaryAction,
}: {
  title: string;
  icon: string;
  destructive?: boolean;
  onCancel: () => void;
  children?: React.ReactNode;
  body?: React.ReactNode;
  primaryLabel?: string;
  primaryAction?: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-labelledby="user-action-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/30 p-margin-mobile"
    >
      <div className="w-full max-w-[520px] max-h-[calc(100dvh-2rem)] overflow-y-auto bg-surface-container-lowest rounded-xl shadow-lg border border-outline-variant p-8">
        <header className="flex items-center gap-3 mb-6">
          <div
            className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${
              destructive
                ? 'bg-error-container text-on-error-container'
                : 'bg-primary-container text-on-primary'
            }`}
          >
            <Icon name={icon} />
          </div>
          <h2 id="user-action-title" className="font-headline-sm text-headline-sm text-on-surface">
            {title}
          </h2>
        </header>
        {body}
        {children}
        {primaryLabel && primaryAction ? (
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" onClick={primaryAction}>
              {primaryLabel}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
